import { SUGGEST_MIN_CHARS, type SearchSuggestion, type SuggestionKind } from '@powerful/data';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSearchSuggestions } from '../../hooks/use-search-suggestions';
import { Icon, type IconName } from '../icons/icon';

/**
 * Campo de busca da descoberta — combobox ARIA 1.2 com listbox agrupada (C8, canvas `Search`).
 *
 * O foco NUNCA sai do campo: a opção ativa é indicada por `aria-activedescendant`, e as setas
 * percorrem as opções dos três grupos como uma lista só. Enter numa opção executa-a (categoria
 * aplica filtro, serviço pesquisa o nome, negócio abre o perfil); Enter sem opção ativa pesquisa o
 * texto. Esc fecha a lista; um segundo Esc limpa o campo.
 */
export interface SearchBarProps {
  /** Id do rótulo visível (o `<label>` vive no layout da página). */
  labelledBy: string;
  inputId: string;
  initialValue?: string;
  onSubmitText: (text: string) => void;
  onSelectCategory: (categoryId: string) => void;
  onSelectBusiness: (slug: string) => void;
}

const GROUPS: readonly { kind: SuggestionKind; icon: IconName }[] = [
  { kind: 'category', icon: 'grid' },
  { kind: 'service', icon: 'tag' },
  { kind: 'business', icon: 'user' },
];

const foldChar = (char: string) => char.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Destaca o trecho digitado ignorando acentos e caixa: «depil» em «Depilação». */
export function highlight(label: string, query: string): ReactNode {
  const q = [...query.trim()].map(foldChar).join('');
  if (!q) return label;
  const chars = [...label];
  const folded = chars.map(foldChar);
  const joined = folded.join('');
  const at = joined.indexOf(q);
  if (at < 0) return label;
  // Converte a posição na string dobrada de volta para índices de caractere do rótulo.
  let pos = 0;
  let start = -1;
  let end = chars.length;
  for (let i = 0; i < chars.length; i += 1) {
    if (start < 0 && pos >= at) start = i;
    pos += folded[i]!.length;
    if (start >= 0 && pos >= at + q.length) {
      end = i + 1;
      break;
    }
  }
  if (start < 0) return label;
  return (
    <>
      {chars.slice(0, start).join('')}
      <b>{chars.slice(start, end).join('')}</b>
      {chars.slice(end).join('')}
    </>
  );
}

const usefulLength = (text: string) => [...text].map(foldChar).join('').replace(/[^a-z0-9]+/g, '').length;

export function SearchBar({ labelledBy, inputId, initialValue = '', onSubmitText, onSelectCategory, onSelectBusiness }: SearchBarProps) {
  const { t } = useTranslation();
  const listId = useId();
  const optionPrefix = useId();
  const [text, setText] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { suggestions, status, isOffline } = useSearchSuggestions(text);

  // Back/Forward e navegação por chip trocam a consulta da URL: o campo acompanha.
  useEffect(() => setText(initialValue), [initialValue]);

  const grouped = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: suggestions.filter((s) => s.kind === g.kind) })).filter((g) => g.items.length > 0),
    [suggestions],
  );
  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);
  useEffect(() => setActive(-1), [suggestions]);

  const trimmed = text.trim();
  const short = trimmed.length > 0 && usefulLength(trimmed) < SUGGEST_MIN_CHARS;
  const showList = open && status === 'success' && flat.length > 0;
  const showPanel = open && trimmed.length > 0 && (short || status !== 'idle' || isOffline);

  const choose = (s: SearchSuggestion) => {
    setOpen(false);
    if (s.kind === 'category' && s.category_id) onSelectCategory(s.category_id);
    else if (s.kind === 'business') onSelectBusiness(s.target);
    else {
      setText(s.label);
      onSubmitText(s.label);
    }
  };

  const submit = () => {
    setOpen(false);
    onSubmitText(trimmed);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) setOpen(true);
        if (flat.length === 0) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i < 0 ? (step > 0 ? 0 : flat.length - 1) : (i + step + flat.length) % flat.length));
        return;
      }
      case 'Enter': {
        event.preventDefault();
        const option = showList && active >= 0 ? flat[active] : undefined;
        if (option) choose(option);
        else submit();
        return;
      }
      case 'Escape':
        if (open && trimmed) {
          event.preventDefault();
          setOpen(false);
          setActive(-1);
        } else if (text) {
          event.preventDefault();
          setText('');
        }
        return;
      default:
    }
  };

  const optionId = (index: number) => `${optionPrefix}-opt-${index}`;
  let running = -1;

  let panel: ReactNode = null;
  if (showPanel) {
    if (short) panel = <p className="pc-muted" role="status">{t('discovery.search.minChars')}</p>;
    else if (isOffline) panel = <p className="pc-muted" role="status">{t('discovery.search.offline')}</p>;
    else if (status === 'loading') {
      panel = (
        <p className="pc-muted flex items-center gap-2" role="status">
          <span className="pc-spin" aria-hidden="true" />
          {t('discovery.search.loading')}
        </p>
      );
    } else if (status === 'error') {
      panel = (
        <div className="alert alert-warn min-w-0" role="status">
          <span className="text-warn"><Icon name="alert" size={18} /></span>
          <span className="min-w-0 text-[length:var(--t-sm)]">{t('discovery.search.suggestError')}</span>
        </div>
      );
    } else if (status === 'success' && flat.length === 0) {
      panel = (
        <div className="flex flex-col items-start gap-2" role="status">
          <p className="pc-body">{t('discovery.search.none', { query: trimmed })}</p>
          <p className="pc-muted">{t('discovery.search.noneHint')}</p>
          <button className="btn btn-secondary btn-sm hit" type="button" onClick={submit}>
            {t('discovery.search.searchAnyway', { query: trimmed })}
          </button>
        </div>
      );
    }
  }

  return (
    <div className="pc-combo">
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="input pc-search">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            role="combobox"
            aria-labelledby={labelledBy}
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
            autoComplete="off"
            enterKeyHint="search"
            maxLength={60}
            placeholder={t('discovery.search.placeholder')}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
          />
          {text ? (
            <button
              className="hit pc-clear"
              type="button"
              aria-label={t('discovery.search.clear')}
              // `mousedown` e não `click`: impede o blur do campo antes de limpar (a lista fecharia).
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setText('');
                inputRef.current?.focus();
              }}
            >
              <Icon name="close" size={18} />
            </button>
          ) : null}
        </div>
      </form>

      {/* A listbox existe sempre no DOM: `aria-controls` nunca aponta para um id órfão. */}
      <div className={showList || panel ? 'pc-dropdown' : 'hidden'} data-testid="search-dropdown">
        <ul id={listId} role="listbox" aria-label={t('discovery.search.suggestionsFor', { query: trimmed })} className="pc-listbox" hidden={!showList}>
          {grouped.map((group) => {
            const labelId = `${listId}-${group.kind}`;
            return (
              <li key={group.kind} role="presentation">
                <ul role="group" aria-labelledby={labelId} className="pc-group">
                  <li role="presentation" id={labelId} className="pc-group-label">
                    {t(`discovery.search.groups.${group.kind}`)}
                  </li>
                  {group.items.map((s) => {
                    running += 1;
                    const index = running;
                    return (
                      // Padrão combobox ARIA 1.2 com `aria-activedescendant`: o foco nunca sai do
                      // campo, e é ELE que trata setas/Enter/Esc (`onKeyDown` acima). A opção só
                      // recebe o clique de rato/toque — um handler de teclado aqui nunca dispararia.
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                      <li
                        key={`${s.kind}-${s.target}`}
                        id={optionId(index)}
                        role="option"
                        aria-selected={index === active}
                        className="pc-option"
                        // `mousedown` evita o blur do campo, que fecharia a lista antes do clique.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => choose(s)}
                        onMouseEnter={() => setActive(index)}
                      >
                        <span className="pc-opt-ic"><Icon name={group.icon} size={18} /></span>
                        <span className="pc-opt-txt">{highlight(s.label, trimmed)}</span>
                        {s.detail ? <span className="pc-opt-meta">{s.detail}</span> : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
        {panel}
      </div>
      <span className="pc-vh" role="status" aria-live="polite">
        {showList ? t('discovery.search.available', { count: flat.length }) : ''}
      </span>
    </div>
  );
}
